import { templateIdForServer } from '@electron/shared/mcp-template-gating';

import type {
  CachedConnectedAccount,
  IntegrationInfo,
} from '@/features/integrations/integrations.types';

import type { ServerEntry } from '@littlebird/axon-mcp';
import type {
  ComposioAccount,
  ComposioCatalogItem,
  LocalIntegration,
  McpAccount,
  McpCatalogItem,
} from '@littlebird/integrations';
import type { CachedMCPServer, MCPCatalogEntry } from '@littlebird/mcp';

/** A device toolkit this desktop knows how to render, with its current OS permission grant. */
export type NativeDeviceToolkitInput = {
  info: IntegrationInfo;
  granted: boolean;
};

type AppleNativeToolkit =
  | 'applereminders'
  | 'applecontacts'
  // Not Apple-native in fact; rides the same wiring because the toggle IS the
  // grant (no OS permission dialog). Rename deferred.
  | 'terminal';

const APPLE_NATIVE_TOOLKITS = new Set<string>([
  'applereminders',
  'applecontacts',
  'terminal',
]);

function isAppleNativeToolkit(
  toolkitName: string
): toolkitName is AppleNativeToolkit {
  return APPLE_NATIVE_TOOLKITS.has(toolkitName);
}

// Composio and MCP rows both ride under an internal `__auto_*` placeholder label
// while their OAuth flow runs; the accounts tab must fall through to the identity
// hint or provider name instead of naming the account after the placeholder
export const displayableLabel = (label: string): string =>
  label.startsWith('__auto_') ? '' : label;

export const toComposioAccounts = (
  available: readonly IntegrationInfo[],
  accounts: readonly CachedConnectedAccount[]
): ComposioAccount[] =>
  accounts.flatMap((account) => {
    const info = available.find(
      (item) => item.toolkit_name === account.toolkit_name
    );
    return info && !info.on_device
      ? [
          {
            id: account.id,
            toolkitSlug: account.toolkit_name,
            displayName: info.display_name,
            accountLabel: displayableLabel(account.account_label),
            identityHint:
              typeof account.connection_details.email === 'string'
                ? account.connection_details.email
                : null,
            enabled: account.is_user_enabled,
            status: account.connection_status,
          },
        ]
      : [];
  });

export const toComposioCatalog = (
  available: readonly IntegrationInfo[]
): ComposioCatalogItem[] =>
  available
    .filter((info) => !info.on_device)
    .map((info) => ({
      toolkitSlug: info.toolkit_name,
      displayName: info.display_name,
      description: info.description,
      iconUrl: info.logo_url ?? null,
    }));

export const toMcpAccounts = (
  servers: readonly CachedMCPServer[]
): McpAccount[] =>
  servers.map((server) => ({
    id: server.id,
    slug: server.slug,
    name: server.name,
    endpoint: server.url,
    enabled: server.enabled,
    status: server.status,
    label: displayableLabel(server.account_label),
    identityHint: server.account_identity_hint,
    iconUrl: server.icon_url || null,
  }));

export const toMcpCatalog = (
  entries: readonly MCPCatalogEntry[]
): McpCatalogItem[] =>
  entries.map((entry) => ({
    id: entry.id,
    name: entry.name,
    description: entry.description,
    endpoint: entry.url,
    iconUrl: entry.icon_url,
  }));

function localMcpStatus(server: ServerEntry): LocalIntegration['status'] {
  // A failed connection test or setup run is the only signal available without
  // the live "setting up" progress state
  if (
    server.lastConnectionTest?.ok === false ||
    server.lastSetup?.ok === false
  ) {
    return 'needs_attention';
  }
  return 'connected';
}

function toLocalMcpIntegration(server: ServerEntry): LocalIntegration {
  return {
    id: server.slug,
    toolkitSlug: server.slug,
    name: server.name,
    description: server.description ?? '',
    iconUrl: null,
    kind: 'local_mcp',
    enabled: server.enabled !== false,
    status: localMcpStatus(server),
    settings: [
      {
        kind: 'local_mcp',
        installationId: server.slug,
        serverSlug: server.slug,
        trustedTemplateId: templateIdForServer(server),
      },
    ],
  };
}

/** The device's axon manifest registers the built-in Apple toolkits under
 * these ids (packages/axon-toolkits), and the backend's tool inventory
 * persists their listings under the same spelling. The on-device catalog
 * spells the identical toolkits without underscores — a UI-only label baked
 * into shipped clients — so the provider key (which the backend joins on)
 * must use the manifest spelling, while `settings.toolkit` keeps the catalog
 * spelling for the local permission/config wiring. */
export const AXON_MANIFEST_TOOLKIT_IDS: Record<AppleNativeToolkit, string> = {
  applereminders: 'apple_reminders',
  applecontacts: 'apple_contacts',
  terminal: 'terminal',
};

function toDeviceToolkitIntegration({
  info,
  granted,
  toolkit,
}: NativeDeviceToolkitInput & {
  toolkit: AppleNativeToolkit;
}): LocalIntegration {
  return {
    id: info.toolkit_name,
    toolkitSlug: AXON_MANIFEST_TOOLKIT_IDS[toolkit],
    name: info.display_name,
    description: info.description,
    iconUrl: info.logo_url ?? null,
    kind: 'device_toolkit',
    enabled: granted,
    status: granted ? 'connected' : 'needs_attention',
    settings: [{ kind: 'apple_native', toolkit }],
  };
}

/** Maps this desktop's local MCP servers and native device toolkits into the
 * package's `LocalIntegration` shape. Never spreads `ServerEntry.transport`
 * or a permission object — those carry paths/env/headers that must stay
 * on-device. */
export const toLocalIntegrations = ({
  localServers,
  nativeIntegrations,
}: {
  localServers: readonly ServerEntry[];
  nativeIntegrations: readonly NativeDeviceToolkitInput[];
}): LocalIntegration[] => [
  ...localServers.map(toLocalMcpIntegration),
  ...nativeIntegrations.flatMap((native) =>
    isAppleNativeToolkit(native.info.toolkit_name)
      ? [
          toDeviceToolkitIntegration({
            ...native,
            toolkit: native.info.toolkit_name,
          }),
        ]
      : []
  ),
];
